import { compileFn } from "./mathEval";
import { parseDomain, xInDomain } from "./graphDomain";

export type GraphRange = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
};

/** Клетка как в ОГЭ/ЕГЭ: единицы по осям. */
export const DEFAULT_GRAPH_RANGE: GraphRange = {
  xMin: -8,
  xMax: 8,
  yMin: -8,
  yMax: 8,
};

export const GRAPH_SERIES_COLORS = [
  "#6246ea",
  "#dc2626",
  "#2563eb",
  "#16a34a",
  "#ca8a04",
] as const;

export type GraphSeriesPlot = {
  expr: string;
  domain?: string;
  color?: string;
  /** false — не рисовать (как «глаз» в Desmos). */
  visible?: boolean;
};

export type GraphMarkPlot = {
  x: number;
  y: number;
  filled: boolean;
  color?: string;
};

const HALF_SPAN_MIN = 1.5;
const HALF_SPAN_MAX = 40;

function clampHalfSpans(hx: number, hy: number): { hx: number; hy: number } {
  return {
    hx: Math.min(HALF_SPAN_MAX, Math.max(HALF_SPAN_MIN, hx)),
    hy: Math.min(HALF_SPAN_MAX, Math.max(HALF_SPAN_MIN, hy)),
  };
}

export function zoomGraphRange(range: GraphRange, factor: number): GraphRange {
  const cx = (range.xMin + range.xMax) / 2;
  const cy = (range.yMin + range.yMax) / 2;
  const { hx, hy } = clampHalfSpans(
    ((range.xMax - range.xMin) / 2) * factor,
    ((range.yMax - range.yMin) / 2) * factor
  );
  return {
    xMin: cx - hx,
    xMax: cx + hx,
    yMin: cy - hy,
    yMax: cy + hy,
  };
}

/** Метрики отрисовки — те же, что в paintGraph (равные клетки). */
export function graphViewLayout(cssW: number, cssH: number, range: GraphRange) {
  const xSpan = range.xMax - range.xMin || 1;
  const ySpan = range.yMax - range.yMin || 1;
  const unit = Math.min(cssW / xSpan, cssH / ySpan);
  const plotW = xSpan * unit;
  const plotH = ySpan * unit;
  const ox = (cssW - plotW) / 2;
  const oy = (cssH - plotH) / 2;
  return { xSpan, ySpan, unit, plotW, plotH, ox, oy };
}

export function graphClientToWorld(
  cssX: number,
  cssY: number,
  cssW: number,
  cssH: number,
  range: GraphRange
): { x: number; y: number } {
  const { xSpan, ySpan, plotW, plotH, ox, oy } = graphViewLayout(cssW, cssH, range);
  return {
    x: range.xMin + ((cssX - ox) / plotW) * xSpan,
    y: range.yMin + (1 - (cssY - oy) / plotH) * ySpan,
  };
}

/** Сдвиг в пикселях canvas (как pan мышью). */
export function panGraphRange(
  range: GraphRange,
  dPx: number,
  dPy: number,
  cssW: number,
  cssH: number
): GraphRange {
  const { unit } = graphViewLayout(cssW, cssH, range);
  if (!(unit > 0)) return range;
  const dx = -dPx / unit;
  const dy = dPy / unit;
  return {
    xMin: range.xMin + dx,
    xMax: range.xMax + dx,
    yMin: range.yMin + dy,
    yMax: range.yMax + dy,
  };
}

/** Зум относительно точки мира (курсор) — как колёсико в Desmos. */
export function zoomGraphRangeAt(
  range: GraphRange,
  factor: number,
  anchorX: number,
  anchorY: number
): GraphRange {
  if (!(factor > 0) || !Number.isFinite(factor)) return range;
  const xMin = anchorX + (range.xMin - anchorX) * factor;
  const xMax = anchorX + (range.xMax - anchorX) * factor;
  const yMin = anchorY + (range.yMin - anchorY) * factor;
  const yMax = anchorY + (range.yMax - anchorY) * factor;
  const { hx, hy } = clampHalfSpans((xMax - xMin) / 2, (yMax - yMin) / 2);
  const rawHx = (xMax - xMin) / 2;
  const rawHy = (yMax - yMin) / 2;
  if (Math.abs(hx - rawHx) < 1e-9 && Math.abs(hy - rawHy) < 1e-9) {
    return { xMin, xMax, yMin, yMax };
  }
  const cx = (xMin + xMax) / 2;
  const cy = (yMin + yMax) / 2;
  return {
    xMin: cx - hx,
    xMax: cx + hx,
    yMin: cy - hy,
    yMax: cy + hy,
  };
}

function formatTick(n: number): string {
  if (Math.abs(n) < 1e-9) return "0";
  const r = Math.round(n * 1000) / 1000;
  return String(r);
}

const TRIG_FN_RE = /\b(sin|cos|tan|cot|asin|acos|atan|acot)\s*\(/i;
const X_TRIG_STEP = Math.PI / 2;

function seriesUsesTrig(series: GraphSeriesPlot[]): boolean {
  return series.some((s) => {
    const e = s.expr.trim();
    return e.length > 0 && TRIG_FN_RE.test(e);
  });
}

/** Подпись деления оси x в режиме тригонометрии (шаг π/2). */
function formatTrigTick(x: number): string {
  const n = Math.round(x / X_TRIG_STEP);
  if (Math.abs(x - n * X_TRIG_STEP) > 1e-5) return formatTick(x);
  if (n === 0) return "0";
  const sign = n < 0 ? "−" : "";
  const absN = Math.abs(n);
  if (absN === 1) return `${sign}π/2`;
  if (absN === 2) return `${sign}π`;
  if (absN % 2 === 1) return `${sign}${absN}π/2`;
  return `${sign}${absN / 2}π`;
}

function xGridStep(xSpan: number, trig: boolean): number {
  if (trig) return X_TRIG_STEP;
  return labelStep(xSpan);
}

function labelStep(span: number): number {
  if (span <= 16) return 1;
  if (span <= 32) return 2;
  return niceStep(span / 8);
}

function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const exp = Math.floor(Math.log10(raw));
  const f = raw / Math.pow(10, exp);
  let nf = 1;
  if (f > 5) nf = 10;
  else if (f > 2) nf = 5;
  else if (f > 1) nf = 2;
  return nf * Math.pow(10, exp);
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const len = 8;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - len * Math.cos(ang - 0.4), y2 - len * Math.sin(ang - 0.4));
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - len * Math.cos(ang + 0.4), y2 - len * Math.sin(ang + 0.4));
  ctx.stroke();
}

export function paintGraph(
  canvas: HTMLCanvasElement,
  series: GraphSeriesPlot[],
  range: GraphRange,
  marks: GraphMarkPlot[] = []
): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const cssW = Math.max(40, canvas.clientWidth || 280);
  const cssH = Math.max(40, canvas.clientHeight || 200);
  canvas.width = Math.max(1, Math.round(cssW * dpr));
  canvas.height = Math.max(1, Math.round(cssH * dpr));
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);
  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(0, 0, cssW, cssH);

  const xMin = range.xMin;
  const xMax = range.xMax;
  const yMin = range.yMin;
  const yMax = range.yMax;
  const xSpan = xMax - xMin || 1;
  const ySpan = yMax - yMin || 1;

  // Одна длина клетки по X и Y (как в тетрадном листе / ОГЭ).
  const unit = Math.min(cssW / xSpan, cssH / ySpan);
  const plotW = xSpan * unit;
  const plotH = ySpan * unit;
  const ox = (cssW - plotW) / 2;
  const oy = (cssH - plotH) / 2;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(ox, oy, plotW, plotH);

  const toPx = (x: number, y: number) => ({
    px: ox + ((x - xMin) / xSpan) * plotW,
    py: oy + plotH - ((y - yMin) / ySpan) * plotH,
  });

  const trigX = seriesUsesTrig(series);
  const gridX = xGridStep(xSpan, trigX);
  const gridY = labelStep(ySpan);
  const formatXTick = trigX ? formatTrigTick : formatTick;
  ctx.save();
  ctx.beginPath();
  ctx.rect(ox, oy, plotW, plotH);
  ctx.clip();
  ctx.strokeStyle = "#e2e8f0";
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = Math.ceil(xMin / gridX) * gridX; x <= xMax + 1e-9; x += gridX) {
    const { px } = toPx(x, 0);
    ctx.moveTo(px, oy);
    ctx.lineTo(px, oy + plotH);
  }
  for (let y = Math.ceil(yMin / gridY) * gridY; y <= yMax + 1e-9; y += gridY) {
    const { py } = toPx(0, y);
    ctx.moveTo(ox, py);
    ctx.lineTo(ox + plotW, py);
  }
  ctx.stroke();

  ctx.strokeStyle = "#334155";
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  const o = toPx(0, 0);
  const axisY = Math.min(oy + plotH - 1, Math.max(oy, o.py));
  const axisX = Math.min(ox + plotW - 1, Math.max(ox, o.px));
  ctx.moveTo(ox, axisY);
  ctx.lineTo(ox + plotW, axisY);
  ctx.moveTo(axisX, oy);
  ctx.lineTo(axisX, oy + plotH);
  ctx.stroke();
  drawArrow(ctx, ox, axisY, ox + plotW - 1, axisY);
  drawArrow(ctx, axisX, oy + plotH, axisX, oy + 1);

  const tick = 5;
  ctx.strokeStyle = "#475569";
  ctx.fillStyle = "#475569";
  ctx.lineWidth = 1.25;
  ctx.font = "10px Segoe UI, system-ui, sans-serif";

  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  for (let x = Math.ceil(xMin / gridX) * gridX; x <= xMax + 1e-9; x += gridX) {
    if (Math.abs(x) < gridX * 1e-6) continue;
    const { px } = toPx(x, 0);
    ctx.beginPath();
    ctx.moveTo(px, axisY - tick);
    ctx.lineTo(px, axisY + tick);
    ctx.stroke();
    const labelY = axisY + tick + 2;
    if (labelY < cssH - 12) {
      ctx.fillText(formatXTick(x), px, labelY);
    } else {
      ctx.textBaseline = "bottom";
      ctx.fillText(formatXTick(x), px, axisY - tick - 2);
      ctx.textBaseline = "top";
    }
  }

  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  for (let y = Math.ceil(yMin / gridY) * gridY; y <= yMax + 1e-9; y += gridY) {
    if (Math.abs(y) < gridY * 1e-6) continue;
    const { py } = toPx(0, y);
    ctx.beginPath();
    ctx.moveTo(axisX - tick, py);
    ctx.lineTo(axisX + tick, py);
    ctx.stroke();
    const labelX = axisX - tick - 3;
    if (labelX > 8) {
      ctx.fillText(formatTick(y), labelX, py);
    } else {
      ctx.textAlign = "left";
      ctx.fillText(formatTick(y), axisX + tick + 3, py);
      ctx.textAlign = "right";
    }
  }

  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText("0", axisX - 4, axisY + 4);
  ctx.font = "11px Segoe UI, system-ui, sans-serif";
  ctx.fillStyle = "#64748b";
  ctx.textAlign = "left";
  ctx.textBaseline = "bottom";
  ctx.fillText("x", ox + plotW - 12, axisY - 6);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("y", axisX + 6, oy + 4);

  const samples = Math.max(220, Math.floor(plotW * 3));
  for (let si = 0; si < series.length; si++) {
    const spec = series[si]!;
    if (spec.visible === false) continue;
    const fnExpr = spec.expr.trim().replace(/\|([^|]+)\|/g, "abs($1)");
    if (!fnExpr) continue;
    const fn = compileFn(fnExpr, "rad");
    const domain = parseDomain(spec.domain ?? "");
    const color = spec.color || GRAPH_SERIES_COLORS[si % GRAPH_SERIES_COLORS.length]!;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.15;
    ctx.beginPath();
    let drawing = false;
    let prevY = NaN;
    for (let i = 0; i <= samples; i++) {
      const x = xMin + (xSpan * i) / samples;
      if (!xInDomain(x, domain)) {
        drawing = false;
        prevY = NaN;
        continue;
      }
      const y = fn(x);
      if (!Number.isFinite(y) || Math.abs(y) > 1e6) {
        drawing = false;
        prevY = NaN;
        continue;
      }
      const { px, py } = toPx(x, y);
      if (Number.isFinite(prevY) && Math.abs(y - prevY) > ySpan * 0.7) {
        drawing = false;
      }
      prevY = y;
      if (py < oy - 80 || py > oy + plotH + 80) {
        drawing = false;
        continue;
      }
      if (!drawing) {
        ctx.moveTo(px, py);
        drawing = true;
      } else {
        ctx.lineTo(px, py);
      }
    }
    ctx.stroke();
  }

  for (const mark of marks) {
    if (!Number.isFinite(mark.x) || !Number.isFinite(mark.y)) continue;
    const { px, py } = toPx(mark.x, mark.y);
    if (px < ox - 12 || px > ox + plotW + 12 || py < oy - 12 || py > oy + plotH + 12) continue;
    const color = mark.color || "#0f172a";
    ctx.beginPath();
    ctx.arc(px, py, 5, 0, Math.PI * 2);
    if (mark.filled) {
      ctx.fillStyle = color;
      ctx.fill();
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = "#fff";
      ctx.stroke();
    } else {
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = color;
      ctx.stroke();
    }
  }
  ctx.restore();
}
